import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@common/Button';
import { Input, Select } from '@common/FormFields';
import { MapPin, Route, ArrowRight, AlertTriangle } from 'lucide-react';
import { logError, logInfo } from '@utils/logger';
import { Toast } from '@common/Toast';

export const OSRouteForm = ({ onRouteCalculated, onCancel }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    origin: '',
    destination: '',
    height: '',
    heightUnit: 'm',
    width: '',
    widthUnit: 'm',
    length: '',
    lengthUnit: 'm',
    weight: '',
    weightUnit: 'kg',
    buffer: 0.1, // Default 10cm buffer
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.origin.trim()) {
        throw new Error('Origin address is required');
      }
      if (!formData.destination.trim()) {
        throw new Error('Destination address is required');
      }
      if (!formData.height || isNaN(parseFloat(formData.height))) {
        throw new Error('Valid vehicle height is required');
      }

      // Convert height to meters if needed
      let heightInMeters = parseFloat(formData.height);
      if (formData.heightUnit === 'ft') {
        heightInMeters = heightInMeters * 0.3048;
      } else if (formData.heightUnit === 'in') {
        heightInMeters = heightInMeters * 0.0254;
      }

      // Add buffer to height
      const heightWithBuffer = heightInMeters + formData.buffer;

      // Log the height calculation with buffer for clarity
      logInfo(`Original height: ${heightInMeters}m, With safety buffer: ${heightWithBuffer}m (buffer: ${formData.buffer}m)`);

      // Prepare route calculation data
      const routeData = {
        origin: formData.origin,
        destination: formData.destination,
        height: heightWithBuffer, // This includes the safety buffer
        originalHeight: {
          value: parseFloat(formData.height),
          unit: formData.heightUnit
        },
        dimensions: {
          width: formData.width ? {
            value: parseFloat(formData.width),
            unit: formData.widthUnit
          } : null,
          length: formData.length ? {
            value: parseFloat(formData.length),
            unit: formData.lengthUnit
          } : null,
          weight: formData.weight ? {
            value: parseFloat(formData.weight),
            unit: formData.weightUnit
          } : null
        },
        buffer: formData.buffer
      };

      // Call the route calculation function
      await onRouteCalculated(routeData);
      
      logInfo('Route calculation initiated with data:', routeData);
      
      // Don't close the form automatically so user can see the results
    } catch (error) {
      logError('Route calculation failed:', error);
      setError(error.message || 'Failed to calculate route. Please try again.');
      setToast({
        type: 'error',
        message: error.message || 'Failed to calculate route'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#2A2B2E] p-1 rounded-lg shadow-lg">
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Origin */}
        <div>
          <label className="block text-sm font-medium text-white mb-1">
            Origin Address
          </label>
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-400 shrink-0" />
            <Input
              value={formData.origin}
              onChange={(e) => setFormData(prev => ({ ...prev, origin: e.target.value }))}
              placeholder="Enter origin address"
              required
              className="bg-gray-700 border-gray-600 text-white flex-1"
            />
          </div>
        </div>

        {/* Destination */}
        <div>
          <label className="block text-sm font-medium text-white mb-1">
            Destination Address
          </label>
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-red-400 shrink-0" />
            <Input
              value={formData.destination}
              onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
              placeholder="Enter destination address"
              required
              className="bg-gray-700 border-gray-600 text-white flex-1"
            />
          </div>
        </div>

        {/* Vehicle Dimensions */}
        <div>
          <h3 className="text-lg font-medium text-white mb-3">Vehicle Dimensions</h3>
          
          {/* Height */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Height (required)
              </label>
              <Input
                type="number"
                value={formData.height}
                onChange={(e) => setFormData(prev => ({ ...prev, height: e.target.value }))}
                placeholder="Enter height"
                required
                min="0"
                step="0.01"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Unit
              </label>
              <Select
                value={formData.heightUnit}
                onChange={(value) => setFormData(prev => ({ ...prev, heightUnit: value }))}
                options={[
                  { value: 'm', label: 'Meters' },
                  { value: 'ft', label: 'Feet' },
                  { value: 'in', label: 'Inches' }
                ]}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>

          {/* Width */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Width (optional)
              </label>
              <Input
                type="number"
                value={formData.width}
                onChange={(e) => setFormData(prev => ({ ...prev, width: e.target.value }))}
                placeholder="Enter width"
                min="0"
                step="0.01"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Unit
              </label>
              <Select
                value={formData.widthUnit}
                onChange={(value) => setFormData(prev => ({ ...prev, widthUnit: value }))}
                options={[
                  { value: 'm', label: 'Meters' },
                  { value: 'ft', label: 'Feet' },
                  { value: 'in', label: 'Inches' }
                ]}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>

          {/* Length */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Length (optional)
              </label>
              <Input
                type="number"
                value={formData.length}
                onChange={(e) => setFormData(prev => ({ ...prev, length: e.target.value }))}
                placeholder="Enter length"
                min="0"
                step="0.01"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Unit
              </label>
              <Select
                value={formData.lengthUnit}
                onChange={(value) => setFormData(prev => ({ ...prev, lengthUnit: value }))}
                options={[
                  { value: 'm', label: 'Meters' },
                  { value: 'ft', label: 'Feet' },
                  { value: 'in', label: 'Inches' }
                ]}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>

          {/* Weight */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Weight (optional)
              </label>
              <Input
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                placeholder="Enter weight"
                min="0"
                step="0.01"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Unit
              </label>
              <Select
                value={formData.weightUnit}
                onChange={(value) => setFormData(prev => ({ ...prev, weightUnit: value }))}
                options={[
                  { value: 'kg', label: 'Kilograms' },
                  { value: 'lbs', label: 'Pounds' },
                  { value: 't', label: 'Metric Tons' }
                ]}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>
        </div>

        {/* Safety Buffer */}
        <div>
          <label className="block text-sm font-medium text-white mb-1">
            Safety Buffer (meters)
          </label>
          <Input
            type="number"
            value={formData.buffer}
            onChange={(e) => setFormData(prev => ({ ...prev, buffer: parseFloat(e.target.value) }))}
            min="0"
            max="1"
            step="0.01"
            className="bg-gray-700 border-gray-600 text-white"
          />
          <p className="text-xs text-gray-400 mt-1">
            Additional clearance buffer for safety (default: 0.1m or 10cm). This is added to your vehicle height for safety margin.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            onClick={onCancel}
            variant="default"
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2"
          >
            <Route className="w-4 h-4" />
            Calculate Route
          </Button>
        </div>
      </form>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default OSRouteForm;